/*
 * cclp-attrib.js — first-party ad attribution → Shopify cart attributes.
 *
 * Why: since ~9/22 recognized Shop Pay buyers are routed to shop.app-hosted
 * checkout; the cross-domain hop drops the storefront session, so Shopify's
 * customer journey records those orders as "direct". Cart attributes travel
 * with the cart into ANY checkout and land on the order as customAttributes,
 * so the true source survives regardless of checkout routing.
 *
 * What it does:
 *  1. On every page view, if the URL carries utm_* / click IDs, store a
 *     "touch" (params + landing path + referrer + timestamp) in localStorage.
 *     Last paid/tagged touch wins; the first touch is kept separately.
 *     Touches expire after 30 days. Untagged page views never overwrite a
 *     tagged touch.
 *  2. Writes the touch to cart attributes (POST /cart/update.js):
 *       - right after every successful /cart/add (fetch — awaited, so it
 *         lands before callers redirect to checkout — and XHR/jQuery),
 *       - once per session on page load if the cart already has items
 *         (covers non-AJAX add forms that land on /cart).
 *     Never creates a cart for a visitor who hasn't added anything.
 *  3. Exposes window.CclpAttrib.permalinkParams() for buy flows that use a
 *     cart permalink (cclp-otp.js) — appended as attributes[key]=value.
 *
 * Keys start with "_" so Shopify hides them from the buyer at checkout.
 * Every step is wrapped so an attribution failure can never block a sale.
 */
(function () {
  "use strict";
  if (window.CclpAttrib) return;

  var STORE_KEY = "cclp_attr_v1";
  var SYNC_KEY = "cclp_attr_synced_v1";
  var TTL_MS = 30 * 24 * 60 * 60 * 1000;
  var SYNC_TIMEOUT_MS = 2000;
  var PARAMS = [
    "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_id",
    "fbclid", "gclid", "gbraid", "wbraid", "gad_source", "gad_campaignid", "ttclid", "trybe", "adv"
  ];
  var MAX_LEN = 255;

  function now() { return Date.now(); }
  function clip(v) { return String(v == null ? "" : v).slice(0, MAX_LEN); }

  var memStore = null; // fallback when localStorage is unavailable (same-page buys still attribute)

  function load() {
    try {
      // Prefer the touch captured on THIS page (memStore): some in-app browsers
      // (Instagram/Facebook) return null from getItem without throwing while
      // setItem fails, so storage alone would lose the touch we just saved.
      var raw = memStore;
      if (!raw) { try { raw = window.localStorage.getItem(STORE_KEY); } catch (e) { raw = null; } }
      var s = raw ? JSON.parse(raw) : null;
      if (!s || !s.last) return null;
      if (now() - (s.last.at || 0) > TTL_MS) return null;
      return s;
    } catch (e) { return null; }
  }

  function save(s) {
    var v = JSON.stringify(s);
    memStore = v;
    try { window.localStorage.setItem(STORE_KEY, v); } catch (e) {}
  }

  function capture() {
    var q;
    try { q = new URLSearchParams(window.location.search); } catch (e) { return; }
    var p = {}, tagged = false;
    PARAMS.forEach(function (k) {
      var v = q.get(k);
      if (v) { p[k] = clip(v); tagged = true; }
    });
    var stored = load();
    if (!tagged && stored) return; // untagged views never overwrite a tagged/earlier touch
    var ref = "";
    try { if (document.referrer && new URL(document.referrer).host !== window.location.host) ref = clip(document.referrer.split("?")[0]); } catch (e) {}
    // No stored touch yet: record this page even on internal navigation
    // (e.g. first page view after deploy/storage clear), so _attr_v reliably
    // means "the capture script ran for this cart".
    var touch = { p: p, path: clip(window.location.pathname), ref: ref, at: now() };
    save({ first: (stored && stored.first) || touch, last: touch });
  }

  var SCHEMA = ["_attr_v"].concat(PARAMS.map(function (k) { return "_" + k; }), [
    "_landing_path", "_landing_at", "_referrer",
    "_first_utm_source", "_first_utm_campaign", "_first_landing_path", "_first_landing_at"
  ]);

  // Full key set: absent keys are "" so a stale value from an earlier touch
  // already on the cart gets cleared (cart attributes MERGE, never replace).
  function withBlanks(a) {
    var out = {};
    SCHEMA.forEach(function (k) { out[k] = a[k] || ""; });
    return out;
  }

  function attrs() {
    var s = load();
    if (!s) return null;
    var a = { _attr_v: "1" };
    var L = s.last, F = s.first || L;
    PARAMS.forEach(function (k) { if (L.p[k]) a["_" + k] = L.p[k]; });
    a._landing_path = L.path;
    a._landing_at = new Date(L.at).toISOString();
    if (L.ref) a._referrer = L.ref;
    if (F !== L && F.at !== L.at) {
      if (F.p.utm_source) a._first_utm_source = F.p.utm_source;
      if (F.p.utm_campaign) a._first_utm_campaign = F.p.utm_campaign;
      a._first_landing_path = F.path;
      a._first_landing_at = new Date(F.at).toISOString();
    }
    return a;
  }

  function hash(a) { return a ? JSON.stringify(a) : ""; }

  var _fetch = window.fetch ? window.fetch.bind(window) : null;

  var pending = null; // in-flight attribute write, if any

  // Buy-point keys: set only by permalink buys (the cart IS that bundle);
  // cleared by any later cart add. Deliberately NOT in SCHEMA, so page-view
  // re-syncs never blank them.
  function sync(opts) {
    var a = attrs();
    if (!a || !_fetch) return Promise.resolve();
    var body = withBlanks(a);
    if (opts && opts.clearBuy) { body._buy_page = ""; body._buy_items = ""; }
    var done = function () { if (pending === race) pending = null; };
    var req = _fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-Cclp-Attrib": "1" },
      body: JSON.stringify({ attributes: body }),
      keepalive: true
    }).then(function (r) {
      if (r && r.ok) { try { window.sessionStorage.setItem(SYNC_KEY, hash(a)); } catch (e) {} }
    }).catch(function () {});
    var timeout = new Promise(function (res) { setTimeout(res, SYNC_TIMEOUT_MS); });
    var race = Promise.race([req, timeout]);
    pending = race;
    race.then(done, done);
    return race;
  }

  // If a shopper hits checkout while a write is still in flight (e.g. /cart
  // page right after a non-AJAX add), wait for it (max SYNC_TIMEOUT_MS), then
  // continue. No-op when nothing is pending, so normal checkouts are untouched.
  function isCheckoutTarget(url) {
    try { return /\/checkout/.test(new URL(url, window.location.href).pathname); } catch (e) { return false; }
  }
  document.addEventListener("submit", function (e) {
    var f = e.target;
    if (!pending || !f || f.__cclpHeld) return;
    var sub = e.submitter;
    var goesToCheckout = isCheckoutTarget(f.getAttribute("action") || "") || (sub && sub.name === "checkout");
    if (!goesToCheckout) return;
    // Hold this submit, then re-fire it normally so every other handler
    // (ajax-cart, Recharge action swap, etc.) runs exactly as before.
    e.preventDefault();
    e.stopImmediatePropagation();
    var go = function () {
      f.__cclpHeld = true;
      try {
        if (f.requestSubmit) { sub && sub.form === f ? f.requestSubmit(sub) : f.requestSubmit(); }
        else { HTMLFormElement.prototype.submit.call(f); }
      } finally { f.__cclpHeld = false; }
    };
    pending.then(go, go);
  }, true);
  document.addEventListener("click", function (e) {
    if (!pending || e.defaultPrevented) return;
    var el = e.target && e.target.closest ? e.target.closest("a[href]") : null;
    if (!el || !isCheckoutTarget(el.getAttribute("href"))) return;
    e.preventDefault();
    var href = el.href;
    var go = function () { window.location.href = href; };
    pending.then(go, go);
  }, true);

  function isCartAdd(url) {
    try {
      var u = new URL(url, window.location.href);
      return u.host === window.location.host && /\/cart\/add(\.js)?$/.test(u.pathname);
    } catch (e) { return false; }
  }

  // fetch: await the attribute write before handing the add response back,
  // so flows that redirect to /checkout right after adding still carry it.
  if (_fetch) {
    window.fetch = function (input, init) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var p = _fetch(input, init);
      if (!isCartAdd(url)) return p;
      return p.then(function (resp) {
        if (!resp || !resp.ok) return resp;
        return sync({ clearBuy: true }).then(function () { return resp; }, function () { return resp; });
      });
    };
  }

  // XHR (jQuery $.ajax in ajax-cart.js + older PDP heroes): fire after load.
  try {
    var XO = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (XO) {
      var _open = XO.open, _send = XO.send;
      XO.open = function (method, url) { this.__cclpAdd = isCartAdd(url); return _open.apply(this, arguments); };
      XO.send = function () {
        if (this.__cclpAdd) {
          this.addEventListener("load", function () { if (this.status >= 200 && this.status < 300) sync({ clearBuy: true }); });
        }
        return _send.apply(this, arguments);
      };
    }
  } catch (e) {}

  // Existing cart with items (e.g. non-AJAX add → /cart page, returning visitor):
  // sync once per session per attribution state.
  // Read the cart and write attributes if it has items and they're missing or
  // stale. force=true skips the once-per-session shortcut (used after a cart
  // write we observed but didn't make — the cart may be brand new).
  function syncExistingCart(force, opts) {
    var a = attrs();
    if (!a || !_fetch) return;
    if (!force) { try { if (window.sessionStorage.getItem(SYNC_KEY) === hash(a)) return; } catch (e) {} }
    var chain = _fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        if (!c || !c.item_count) return;
        var have = c.attributes || {};
        var full = withBlanks(a);
        var same = Object.keys(full).every(function (k) { return (have[k] || "") === full[k]; });
        if (opts && opts.clearBuy && (have._buy_page || have._buy_items)) same = false;
        if (same) { try { window.sessionStorage.setItem(SYNC_KEY, hash(a)); } catch (e) {} return; }
        return sync(opts);
      })
      .catch(function () {});
    // Guard checkout clicks for the whole read→write chain, not just the write.
    var capped = Promise.race([chain, new Promise(function (res) { setTimeout(res, SYNC_TIMEOUT_MS * 2); })]);
    pending = capped;
    capped.then(function () { if (pending === capped) pending = null; });
  }

  function permalinkParams(extra) {
    var a = attrs();
    if (!a) return "";
    var full = withBlanks(a);
    full._buy_page = clip(window.location.pathname);
    full._buy_items = extra && extra._buy_items ? clip(extra._buy_items) : "";
    return Object.keys(full).map(function (k) {
      return "attributes" + encodeURIComponent("[" + k + "]") + "=" + encodeURIComponent(full[k]);
    }).join("&");
  }

  try { capture(); } catch (e) {}
  function fullAttrs() { var a = attrs(); return a ? withBlanks(a) : null; }
  window.CclpAttrib = { attrs: attrs, fullAttrs: fullAttrs, sync: sync, permalinkParams: permalinkParams, checkoutViaCart: function () { return checkoutViaCart.apply(null, arguments); } };
  // Safety net for cart writes this script can't wrap: an app that grabbed the
  // native fetch before we loaded (Rebuy Smart Cart), or the Storefront
  // GraphQL cart API. The browser's resource timeline sees every request no
  // matter who made it; after any cart mutation, re-check the cart.
  var recheckTimer = null;
  var suppressObserverUntil = 0; // checkoutViaCart writes the final attributes itself
  function recheckSoon() {
    if (Date.now() < suppressObserverUntil) return;
    clearTimeout(recheckTimer);
    recheckTimer = setTimeout(function () { syncExistingCart(true, { clearBuy: recheckIsAdd }); recheckIsAdd = false; }, 300);
  }
  var recheckIsAdd = false;
  function isCartMutation(url) {
    try {
      var path = new URL(url, window.location.href).pathname;
      return /\/cart\/(add|change|update)(\.js)?$/.test(path) || /\/api\/[^/]+\/graphql(\.json)?$/.test(path);
    } catch (e) { return false; }
  }
  try {
    if (window.PerformanceObserver) {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (e) {
          if ((e.initiatorType === "fetch" || e.initiatorType === "xmlhttprequest") && isCartMutation(e.name)) {
            if (/\/cart\/add(\.js)?(\?|$)/.test(e.name.split("#")[0])) recheckIsAdd = true;
            recheckSoon();
          }
        });
      }).observe({ type: "resource", buffered: true });
    }
  } catch (e) {}

  // Cart-drawer checkout buttons (Rebuy Smart Cart etc.) navigate from JS, so
  // the form/link guard above can't see them. While a write is in flight,
  // hold the click and replay it once the write lands (max SYNC_TIMEOUT_MS).
  var CHECKOUT_BTN = '[name="checkout"], .rebuy-cart__checkout-button, [class*="checkout-button"], [data-checkout-button]';
  document.addEventListener("click", function (e) {
    if (!pending || e.__cclpReplay) return;
    var el = e.target && e.target.closest ? e.target.closest(CHECKOUT_BTN) : null;
    if (!el || el.tagName === "FORM" || (el.form && el.type === "submit")) return; // forms: handled by the submit guard
    e.preventDefault();
    e.stopImmediatePropagation();
    var go = function () { var ev = new MouseEvent("click", { bubbles: true, cancelable: true }); ev.__cclpReplay = true; el.dispatchEvent(ev); };
    pending.then(go, go);
  }, true);

  // Cart-permalink LINKS (lp-layout "Order Now" cards on PF v3 etc., Replo
  // buttons): the link itself builds the cart and jumps to checkout, so no page
  // code gets a chance to write attributes. Stamp them onto the link at click
  // time; Shopify carries permalink attributes onto the order (incl. shop.app).
  // Skips subscription links (selling_plan / the sub-LP flavor CTA, which
  // cclp.js always intercepts). Only our own keys are added or replaced.
  var OWN_KEY = /^attributes\[(_[a-z_]+|lp_origin)\]$/;
  function stampPermalink(e) {
    try {
      var el = e.target && e.target.closest ? e.target.closest("a[href]") : null;
      if (!el || el.hasAttribute("data-flavor-cta")) return;
      var u = new URL(el.getAttribute("href"), window.location.href);
      if (u.host !== window.location.host) return;
      var m = u.pathname.match(/^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/cart\/(\d+:\d+(?:,\d+:\d+)*)\/?$/i);
      if (!m || u.searchParams.has("selling_plan")) return;
      var params = permalinkParams({ _buy_items: m[1] });
      if (!params) return;
      var kept = [];
      u.searchParams.forEach(function (v, k) { if (!OWN_KEY.test(k)) kept.push(encodeURIComponent(k) + "=" + encodeURIComponent(v)); });
      kept.push(params);
      kept.push("attributes" + encodeURIComponent("[lp_origin]") + "=");
      var stamped = u.pathname + "?" + kept.join("&") + u.hash;
      el.setAttribute("href", stamped);
      // Plain same-tab click that goes to checkout -> build the checkout from the cart.
      var rt = u.searchParams.get("return_to");
      var sameTab = e.type === "click" && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
        && (!el.target || el.target === "_self") && !el.hasAttribute("download");
      // Only reroute links we fully understand; anything else keeps the permalink.
      var foreign = {}, known = true;
      u.searchParams.forEach(function (v, k) {
        var fa = k.match(/^attributes\[(.+)\]$/);
        if (fa) { if (!OWN_KEY.test(k)) foreign[fa[1]] = v; }
        else if (k !== "discount" && k !== "return_to") known = false;
      });
      if (sameTab && known && !e.defaultPrevented && (!rt || rt === "/checkout")) {
        e.preventDefault();
        var codes = (u.searchParams.get("discount") || "").split(",").map(function (c) { return c.trim(); }).filter(Boolean);
        checkoutViaCart(m[1].split(","), codes, stamped, m[1], foreign);
      }
    } catch (err) { /* never block the link */ }
  }
  document.addEventListener("click", stampPermalink, true);
  document.addEventListener("auxclick", stampPermalink, true);

  // Checkout built FROM THE CART instead of a cart permalink. Shop Pay /
  // shop.app sometimes builds a permalink checkout without its attributes[...]
  // (OTP permalink orders captured ~75% vs 97-100% for cart-built checkouts,
  // 9/29 order data). So: clear -> add items -> write attributes + discount
  // codes -> /checkout. Any failure or timeout falls back to the permalink,
  // i.e. exactly today's behavior.
  //   items: ["variantId:qty", ...]   codes: ["CODE", ...]   fallbackUrl: permalink
  var viaCartBusy = false;
  window.addEventListener("pageshow", function () { viaCartBusy = false; }); // back button / bfcache
  function checkoutViaCart(items, codes, fallbackUrl, buyItems, extraAttrs) {
    if (viaCartBusy) return;
    viaCartBusy = true;
    var done = false;
    var fallback = function () { if (done) return; done = true; window.location.href = fallbackUrl; };
    var timer = setTimeout(fallback, 8000);
    try {
      var lines = (items || []).map(function (s) {
        var p = String(s).split(":"); return { id: parseInt(p[0], 10), quantity: parseInt(p[1] || "1", 10) };
      }).filter(function (l) { return l.id > 0 && l.quantity > 0; });
      if (!lines.length || !_fetch) { clearTimeout(timer); fallback(); return; }
      try { // keep Rebuy's Smart Cart drawer from popping open mid-redirect
        if (window.Rebuy && window.Rebuy.SmartCart) {
          ["disable", "hide", "close"].forEach(function (m) { if (typeof window.Rebuy.SmartCart[m] === "function") window.Rebuy.SmartCart[m](); });
        }
      } catch (rerr) {}
      suppressObserverUntil = Date.now() + 15000;
      var a = attrs();
      var attributes = a ? withBlanks(a) : {};
      attributes._buy_page = clip(window.location.pathname);
      attributes._buy_items = clip(buyItems || "");
      attributes.lp_origin = "";
      if (extraAttrs) Object.keys(extraAttrs).forEach(function (k) { if (!(k in attributes)) attributes[k] = clip(extraAttrs[k]); });
      var post = function (url, body) {
        return _fetch(url, { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", "X-Cclp-Attrib": "1" }, body: JSON.stringify(body) })
          .then(function (r) { if (!r.ok) throw new Error(url + " " + r.status); return r.json(); });
      };
      var upd = { attributes: attributes };
      if (codes && codes.length) upd.discount = codes.join(",");
      post("/cart/clear.js", {})
        .then(function () { return post("/cart/add.js", { items: lines }); })
        .then(function () { return post("/cart/update.js", upd); })
        .then(function (cart) {
          if (done) return;
          var want = lines.reduce(function (m, l) { m[l.id] = (m[l.id] || 0) + l.quantity; return m; }, {});
          var have = (cart.items || []).reduce(function (m, i) { m[i.variant_id] = (m[i.variant_id] || 0) + i.quantity; return m; }, {});
          var ok = Object.keys(want).every(function (id) { return have[id] >= want[id]; });
          if (!ok) throw new Error("cart mismatch");
          try { window.sessionStorage.setItem(SYNC_KEY, hash(a)); } catch (e) {}
          done = true; clearTimeout(timer);
          window.location.href = "/checkout";
        })
        .catch(function () { clearTimeout(timer); fallback(); });
    } catch (err) { clearTimeout(timer); fallback(); }
  }

  // Start immediately (no DOM needed) so the checkout guard is armed before
  // any checkout button can render.
  syncExistingCart(false);
})();
